import { CheckCircleOutlined } from '@ant-design/icons';
import { ScopeButton } from '@monorepo/shared-lib';
import { Space } from 'antd';
import GenTaxImage from 'assets/images/gen-tax-img-cropped.svg';
import { StyledCard } from 'components/Custom/card';
import { CardDescSpan, CardTitleLargeSpan } from 'components/Custom/spans';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Organisation } from 'store/organisation/models';

interface VatCheckerPanelProps {
  organisation?: Organisation;
  loadCustomersAndSuppliers: (connectionId: string) => void;
  activateVatChecker: (
    connectionId: string,
    orgName: string,
    uuid: string
  ) => void;
  pwaMobileNav?: boolean;
}

export const VatCheckerPanel: React.FC<VatCheckerPanelProps> = ({
  organisation,
  loadCustomersAndSuppliers,
  activateVatChecker,
  pwaMobileNav = false,
}) => {
  const { t } = useTranslation();

  // Installed PWA / PWA navigation: hide VAT number checker everywhere.
  if (pwaMobileNav) return null;

  return (
    <StyledCard
      $nopadding
      fullsize
      className={pwaMobileNav ? 'scope-pwa-vat-checker-panel' : undefined}
      style={{
        boxShadow: '0px 4px 10px 0px #0000000D',
        marginBottom: '20px',
        display: 'flex',
        fontSize: 14,
        fontWeight: 400,
        padding: 0,
      }}
      width="100%"
    >
      <div
        className={
          pwaMobileNav ? 'scope-pwa-vat-checker-panel__body' : undefined
        }
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          flexFlow: pwaMobileNav ? 'column' : 'row',
          alignContent: 'end',
          alignItems: pwaMobileNav ? 'stretch' : undefined,
          padding: pwaMobileNav ? '16px 0' : '24px 24px 0 0',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'space-between',
            padding: pwaMobileNav ? 0 : '0 24px 24px 24px',
            minWidth: 0,
          }}
        >
          <Space wrap align="start">
            <CardTitleLargeSpan style={{ marginBottom: 10 }}>
              {organisation?.vatCheckerActive
                ? t('organisation.vat-checker-activated')
                : t('organisation.activate-vat-checker')}
            </CardTitleLargeSpan>
            {organisation?.vatCheckerActive && (
              <CheckCircleOutlined
                style={{ color: 'var(--color-primary, #41aaaf)', fontSize: 18 }}
              />
            )}
          </Space>
          <CardDescSpan style={{ marginBottom: 16 }}>
            {organisation?.vatCheckerActive
              ? t('organisation.vat-checker-activated-desc')
              : t('organisation.activate-vat-checker-desc')}
          </CardDescSpan>
          <ScopeButton
            type="primary"
            width={pwaMobileNav ? '100%' : '132px'}
            height="40px"
            style={{ fontSize: '13px', fontWeight: 700 }}
            onClick={() => {
              organisation?.vatCheckerActive
                ? loadCustomersAndSuppliers(organisation?.connectionId)
                : activateVatChecker(
                    organisation?.connectionId!,
                    organisation?.friendlyName!,
                    organisation?.uuid!
                  );
            }}
          >
            {organisation?.vatCheckerActive
              ? t('organisation.open-checker')
              : t('organisation.get-started')}
          </ScopeButton>
        </div>

        <img
          alt={t('vat-report.vat-checker-image-alt')}
          src={GenTaxImage}
          className={
            pwaMobileNav ? 'scope-pwa-vat-checker-panel__image' : undefined
          }
          style={
            pwaMobileNav
              ? {
                  width: '100%',
                  maxWidth: 280,
                  height: 'auto',
                  alignSelf: 'center',
                  marginTop: 16,
                }
              : undefined
          }
        />
      </div>
    </StyledCard>
  );
};
